import {
  CreateUserRoleDto,
  PermissionEnum,
  RoleEnum,
  User,
} from '@sr-sdks/api-sdk-axios';
import {
  Button,
  Dialog,
  DropdownMenuItem,
  Icon,
  Tooltip,
  sonner,
} from '@sr/ui-library';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { noOnboardedTeammatesErrorMessage } from '../constant/ErrorMessage';
import { TEAMMATES_MESSAGES } from '../constant/ToasterMessage';
import { useCheckUserPermissions } from '../hooks/useCheckUserPermissions';
import { userRoleService } from '../reducers/permissions/userRoleSlice';
import { userService } from '../reducers/permissions/userSlice';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import { ApiLoadingStateEnum } from '../utils/api/apiLoadingStateEnum';
import {
  isLoadingState,
  useOnSuccess,
  userHighestRole,
  userIsMe,
  userIsWorkspaceOwner,
  userMultipleWorkspaceOwner,
} from '../utils/helpers/common';
import AccessLevelDropdown from './AccessLevelDropdown';
import AppTableV2 from './AppTableV2';
import { ConfirmButtons } from './ConfirmButtons';
import OptionsDropdown from './OptionsDropdown';
import UserNameAndImage from './UserNameAndImage';

const { toast } = sonner;

interface TeammatesTableProps {
  filterElement?: React.ReactNode;
  hasBottomNavigation?: boolean;
  isStaffView?: boolean;
  workspaceId?: string;
}

interface PendingRoleChange {
  roleKey: string;
  userId: string;
  userRoleId?: string;
}

/**
 * Custom hook to manage success toast notifications
 * Encapsulates the toast state and trigger logic
 */
const useSuccessToast = () => {
  const [triggerSuccessToast, setTriggerSuccessToast] = useState<string>('');

  const showSuccessToast = useCallback(() => {
    toast.success('Your teammate has been updated.', {});
    setTriggerSuccessToast('');
  }, []);

  useOnSuccess(triggerSuccessToast, showSuccessToast);

  return setTriggerSuccessToast;
};

/**
 * TeammatesTable Component
 * Displays a table of workspace members with their roles and permissions
 * Allows managing user roles and removing team members
 */
const TeammatesTable = ({
  filterElement,
  hasBottomNavigation = true,
  isStaffView,
  workspaceId,
}: TeammatesTableProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const setTriggerSuccessToast = useSuccessToast();

  // Modal state management
  const [isLeaveWorkspaceModalOpen, setIsLeaveWorkspaceModalOpen] =
    useState(false);
  const [isRemoveTeammateModalOpen, setIsRemoveTeammateModalOpen] =
    useState(false);
  const [isChangeWorkspaceOwnerModalOpen, setIsChangeWorkspaceOwnerModalOpen] =
    useState(false);

  // Workspace and role management state
  const [hasMultipleWorkspaceOwner, setHasMultipleWorkspaceOwner] =
    useState<boolean>(false);
  const [pendingRoleChange, setPendingRoleChange] =
    useState<PendingRoleChange>();
  const [selectedUser, setSelectedUser] = useState<User>();

  // Redux state selectors
  const isLoading = useTypedSelector(userService.selectors.isUsersLoading);
  const isCreating = useTypedSelector(userRoleService.selectors.isCreating);
  const isRemoving = useTypedSelector(userRoleService.selectors.isRemoving);
  const currentUser = useTypedSelector(userService.selectors.userMe);
  const teammates = useTypedSelector(userService.selectors.users);
  const paginationMeta = useTypedSelector(userService.selectors.paginationMeta);
  const allUsersByWorkspace = useTypedSelector(
    userService.selectors.allUsersByWorkspace,
  );

  // Check if current user has permission to change workspace ownership
  const canTransferWorkspaceOwnership = useCheckUserPermissions([
    PermissionEnum.TransferWorkspaceOwnership,
  ]);
  const canEditTeammates = useCheckUserPermissions([
    PermissionEnum.EditTeammates,
  ]);

  /**
   * Handles user leaving the workspace
   * Removes all user roles and redirects to dashboard
   */
  const handleLeaveWorkspace = async (user?: User) => {
    if (!user || !user.roles) return;

    const promises = user.roles.map((userRole) =>
      dispatch(userRoleService.remove(userRole._id)),
    );

    await Promise.all(promises).then(() => {
      navigate('/dashboard');
    });
  };

  const fetchAllTeammates = async (workspaceId: string) => {
    await dispatch(userService.findAllUsersByWorkspace({ workspaceId }));
  };

  /**
   * Fetches teammates for the current page
   * Used for pagination and initial load
   */
  const handlePageRequest = useCallback(
    (page = 1) => {
      if (!workspaceId) return;

      dispatch(userService.findAll({ page, workspaceId }));
    },
    [dispatch, workspaceId],
  );

  /**
   * Handles removing a teammate from the workspace
   * Removes all user roles and refreshes the table
   */
  const handleRemoveClick = async (user?: User) => {
    if (!user || !user.roles) return;

    const promises = user.roles.map((userRole) =>
      dispatch(userRoleService.remove(userRole._id)),
    );

    await Promise.all(promises).then(() => {
      handlePageRequest(paginationMeta?.currentPage);
      setIsRemoveTeammateModalOpen(false);
    });

    toast.success(TEAMMATES_MESSAGES.REMOVED_ONBOARDED, {});
  };

  /**
   * Handles role selection for a user
   * Manages pending role changes and opens workspace owner modal if needed
   */
  const handleRoleSelect = (roleKey: RoleEnum, user: User) => {
    // Reset if selecting the same role
    if (roleKey === userHighestRole(user, workspaceId)?.roleKey) {
      setPendingRoleChange(undefined);
      setSelectedUser(undefined);

      return;
    }

    setSelectedUser(user);

    // Find existing admin role if changing to workspace user
    const existingWorkspaceAdminRole = user.roles?.find(
      (userRole) => userRole.roleKey === RoleEnum.WorkspaceAdmin,
    );

    setPendingRoleChange({
      roleKey,
      userId: user._id,
      userRoleId: existingWorkspaceAdminRole?._id,
    });

    if (roleKey === RoleEnum.WorkspaceOwner) {
      setIsChangeWorkspaceOwnerModalOpen(true);
    }
  };

  /**
   * Removes a workspace role from a user
   * Optionally updates the page after removal
   */
  const handleRemoveWorkspaceRole = async (
    id: string,
    shouldUpdatePage = false,
  ) => {
    if (!id) return;

    const removeUserRole = await dispatch(userRoleService.remove(id));

    if (userRoleService.remove.fulfilled.match(removeUserRole)) {
      setPendingRoleChange(undefined);

      if (shouldUpdatePage) {
        handlePageRequest(paginationMeta?.currentPage);
      }
    }
  };

  /**
   * Handles saving role changes
   * Manages different role change scenarios:
   * - Workspace owner to admin/user
   * - Admin to user
   * - User to admin
   */
  const handleSaveClick = async () => {
    if (!workspaceId || !pendingRoleChange || !selectedUser) return;

    // Handle workspace owner role changes
    if (
      userHighestRole(selectedUser, workspaceId)?.roleKey ===
      RoleEnum.WorkspaceOwner
    ) {
      // Change from owner to admin
      if (pendingRoleChange.roleKey === RoleEnum.WorkspaceAdmin) {
        const findUserOwner = selectedUser?.roles?.find(
          (userRole) => userRole.roleKey === RoleEnum.WorkspaceOwner,
        );

        if (findUserOwner) {
          await handleRemoveWorkspaceRole(findUserOwner._id, true);
        }

        fetchAllTeammates(workspaceId);
        setTriggerSuccessToast(ApiLoadingStateEnum.succeeded);

        return;
      }

      // Change from owner to user
      if (pendingRoleChange.roleKey === RoleEnum.WorkspaceUser) {
        const findUserOwner = selectedUser?.roles?.find(
          (userRole) => userRole.roleKey === RoleEnum.WorkspaceOwner,
        );

        if (findUserOwner) {
          await handleRemoveWorkspaceRole(findUserOwner._id, false);
        }

        const findUserAdmin = selectedUser?.roles?.find(
          (userRole) => userRole.roleKey === RoleEnum.WorkspaceAdmin,
        );

        if (findUserAdmin) {
          await handleRemoveWorkspaceRole(findUserAdmin._id, true);
        }

        fetchAllTeammates(workspaceId);
        setTriggerSuccessToast(ApiLoadingStateEnum.succeeded);

        toast.success(TEAMMATES_MESSAGES.UPDATED_ONBOARDED, {});

        return;
      }
    }

    // Change from admin to user
    if (
      pendingRoleChange.roleKey === RoleEnum.WorkspaceUser &&
      pendingRoleChange.userRoleId
    ) {
      await handleRemoveWorkspaceRole(pendingRoleChange.userRoleId, true);
      setTriggerSuccessToast(ApiLoadingStateEnum.succeeded);

      return;
    }

    // Change from user to admin
    if (pendingRoleChange.roleKey === RoleEnum.WorkspaceAdmin) {
      const createUserRoleDto: CreateUserRoleDto = {
        roleKey: pendingRoleChange.roleKey,
        userId: pendingRoleChange.userId,
        workspaceId: workspaceId,
      };

      const createUserRole = await dispatch(
        userRoleService.create(createUserRoleDto),
      );

      const isFulfilled = userRoleService.create.fulfilled.match;

      if (isFulfilled(createUserRole)) {
        setPendingRoleChange(undefined);
        handlePageRequest(paginationMeta?.currentPage);
      }

      setTriggerSuccessToast(ApiLoadingStateEnum.succeeded);

      return;
    }
  };

  /**
   * Handles changing workspace ownership
   * Creates new owner role and updates the table
   */
  const handleChangeWorkspaceOwnerClick = async () => {
    if (!workspaceId || !pendingRoleChange) return;

    const createUserRoleDto: CreateUserRoleDto = {
      roleKey: RoleEnum.WorkspaceOwner,
      userId: pendingRoleChange.userId,
      workspaceId: workspaceId,
    };

    const createUserRole = await dispatch(
      userRoleService.create(createUserRoleDto),
    );

    const isFulfilled = userRoleService.create.fulfilled.match;

    if (isFulfilled(createUserRole)) {
      if (!isStaffView) {
        // Remove the old current user owner role
        const rolesWithoutOwner =
          currentUser?.roles?.filter(
            (role) =>
              role.roleKey !== RoleEnum.WorkspaceOwner ||
              role.workspaceId !== workspaceId,
          ) ?? [];

        // Update a new current user with the new roles
        const userMe = { ...currentUser, roles: rolesWithoutOwner };

        // Set the new user state
        dispatch(userService.actions.setUserMe(userMe));
      }

      fetchAllTeammates(workspaceId);
      setPendingRoleChange(undefined);
      setIsChangeWorkspaceOwnerModalOpen(false);
      handlePageRequest(paginationMeta?.currentPage);
    }

    setTriggerSuccessToast(ApiLoadingStateEnum.succeeded);
  };

  /**
   * Generates a unique string representation of teammates state
   * Used for detecting changes in the teammates list
   */
  const getTeammatesStateString = (teammates: User[]) => {
    return teammates
      ?.map(
        (user) =>
          user._id +
          ':' +
          (user.roles?.map((role) => role.roleKey).join(',') ?? ''),
      )
      .join('|');
  };

  // Update multiple workspace owner state when teammates change
  useEffect(() => {
    if (allUsersByWorkspace?.length === 0 || !workspaceId) return;

    setHasMultipleWorkspaceOwner(
      userMultipleWorkspaceOwner(allUsersByWorkspace, workspaceId),
    );
  }, [getTeammatesStateString(allUsersByWorkspace), workspaceId]);

  // Fetch all teammates to identify multiple workspace owners
  useEffect(() => {
    if (!workspaceId) return;

    fetchAllTeammates(workspaceId);
  }, [workspaceId]);

  // Initial load of teammates
  useEffect(() => {
    handlePageRequest();
  }, [handlePageRequest, workspaceId]);

  return (
    <>
      <AppTableV2
        id="teammates-table"
        customEmptyTable={{
          description: noOnboardedTeammatesErrorMessage.description,
          icon: noOnboardedTeammatesErrorMessage.icon,
          title: noOnboardedTeammatesErrorMessage.title,
        }}
        filterElement={filterElement ?? <h3>Onboarded</h3>}
        hasBottomNavigation={hasBottomNavigation}
        headerRowElement={
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th className="!w-44">Access Level</th>
            {(canTransferWorkspaceOwnership || !isStaffView) && (
              <th className="!w-10">
                <span className="sr-only">Options</span>
              </th>
            )}
          </tr>
        }
        isLoading={isLoadingState(isLoading)}
        paginationKey="teammatesTablePage"
        paginationMeta={paginationMeta}
        onPageRequest={handlePageRequest}
      >
        {teammates?.map((user) => {
          return (
            <tr key={user._id}>
              <td>
                <UserNameAndImage imageUri={user.imageUri}>
                  {user.firstName} {user.lastName}
                  {userIsMe(user, currentUser) ? <> (You)</> : <></>}
                </UserNameAndImage>
              </td>
              <td className="max-w-[650px] break-words">{user.emailAddress}</td>
              <td className="relative">
                <AccessLevelDropdown
                  hasMultipleWorkspaceOwner={hasMultipleWorkspaceOwner}
                  isPermittedToChangeWorkspaceOwner={
                    canTransferWorkspaceOwnership
                  }
                  isReadOnly={!canEditTeammates}
                  isStaffView={isStaffView}
                  isWorkspaceOwner={userIsWorkspaceOwner(user, workspaceId)}
                  selectedRoleKey={
                    (pendingRoleChange?.userId === user._id
                      ? pendingRoleChange?.roleKey
                      : userHighestRole(user, workspaceId)?.roleKey) as RoleEnum
                  }
                  toggleClassName="pl-0 bg-transparent w-32 hover:bg-transparent"
                  user={user}
                  onRoleSelect={(roleKey) => handleRoleSelect(roleKey, user)}
                />
              </td>
              {(canTransferWorkspaceOwnership || !isStaffView) && (
                <td className="relative !text-center">
                  {userIsWorkspaceOwner(user, workspaceId) ? (
                    <Tooltip
                      content="In order to make changes to this teammate, an owner needs to assign another teammate as the workspace owner."
                      side="left"
                    >
                      <Button
                        icon={<Icon icon="info" weight="fill" />}
                        size="icon"
                        variant="transparent"
                      />
                    </Tooltip>
                  ) : (
                    <OptionsDropdown align="end">
                      {userIsMe(user, currentUser) ? (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setIsLeaveWorkspaceModalOpen(true);
                          }}
                        >
                          Leave Workspace
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedUser(user);
                            setIsRemoveTeammateModalOpen(true);
                          }}
                        >
                          Remove Teammate
                        </DropdownMenuItem>
                      )}
                    </OptionsDropdown>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </AppTableV2>

      {/* Show confirm buttons when role change is pending */}
      {pendingRoleChange &&
        pendingRoleChange.roleKey !== RoleEnum.WorkspaceOwner && (
          <ConfirmButtons
            cancelButtonProps={{
              onClick: () => setPendingRoleChange(undefined),
            }}
            className="mt-10 w-full"
            hasCancelButton
            isLoading={isLoadingState(isRemoving) || isLoadingState(isCreating)}
            saveButtonProps={{
              onClick: handleSaveClick,
            }}
          />
        )}

      {/* Remove teammate confirmation modal */}
      <Dialog
        heading="Are you sure?"
        actionButtonProps={{
          children: 'Remove',
          onClick: () => handleRemoveClick(selectedUser),
        }}
        cancelButtonProps={{
          onClick: () => setIsRemoveTeammateModalOpen(false),
        }}
        open={isRemoveTeammateModalOpen}
        onOpenChange={() => setIsRemoveTeammateModalOpen(false)}
      >
        <div>
          Are you sure you would like to remove &quot;
          <span className="font-bold">
            {selectedUser?.firstName} {selectedUser?.lastName}
          </span>
          &quot;?
        </div>
      </Dialog>

      {/* Leave workspace confirmation modal */}
      <Dialog
        heading="Are you sure?"
        actionButtonProps={{
          children: 'Leave',
          loading: isLoadingState(isRemoving),
          onClick: () => handleLeaveWorkspace(selectedUser),
        }}
        cancelButtonProps={{
          onClick: () => setIsLeaveWorkspaceModalOpen(false),
        }}
        open={isLeaveWorkspaceModalOpen}
        onOpenChange={() => setIsLeaveWorkspaceModalOpen(false)}
      >
        <div className="space-y-4">
          <p>
            Your access to this workspace will be <strong>lost</strong>.
          </p>
          <p>
            To be added back, you will have to be <strong>invited</strong> by an
            admin.
          </p>
          <p>Are you sure you want to continue?</p>
        </div>
      </Dialog>

      {/* Change workspace owner confirmation modal */}
      <Dialog
        heading="Are you sure?"
        actionButtonProps={{
          children: 'Proceed',
          loading: isLoadingState(isCreating),
          onClick: handleChangeWorkspaceOwnerClick,
        }}
        cancelButtonProps={{
          onClick: () => {
            setPendingRoleChange(undefined);
            setIsChangeWorkspaceOwnerModalOpen(false);
          },
        }}
        open={isChangeWorkspaceOwnerModalOpen}
        onOpenChange={() => setIsChangeWorkspaceOwnerModalOpen(false)}
      >
        <p>
          Assigning a teammate as a workspace owner will demote the previous
          owner to an admin.
        </p>
      </Dialog>
    </>
  );
};

export default TeammatesTable;
